import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Snackbar, IconButton, Tooltip } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import NotificationsOffIcon from "@material-ui/icons/NotificationsOff";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import RemoveIcon from "@material-ui/icons/Remove"; // minimize icon
import AddIcon from "@material-ui/icons/Add"; // expand icon
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import config from "./config/config";
import { useLocation } from "react-router-dom";

const NotificationComponent = React.memo(({ enabled = true, setEvents }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [alertData, setAlertData] = useState({ type: "info", message: "" });
  // const [events, setEvents] = useState([]);
  const [muted, setMuted] = useState(false);
  const [minimized, setMinimized] = useState(false); // 👈 new state
  const queueRef = useRef([]);
  const speakingRef = useRef(false);
  const clientRef = useRef(null);
  const showNotification = useMemo(
    () => ["/currentOperationalStatus"].includes(location.pathname),
    [location.pathname]
  );

  /** 🎤 Get Hindi/Indian voice (memoized) */
  const indianVoice = useMemo(() => {
    if (!("speechSynthesis" in window)) return null;
    const voices = speechSynthesis.getVoices();
    return (
      voices.find(
        (v) => v.lang === "en-IN" || v.name.toLowerCase().includes("indian")
      ) || null
    );
  }, []);

  /** 🎤 Speak a message */
  const speakMessage = useCallback(
    (text) => {
      if (muted || !("speechSynthesis" in window)) return;
      const message = text?.replace(
        /(VEHICLE NUMBER\s+)([A-Z0-9]+)/i,
        (_, prefix, vehicle) => `${prefix}${vehicle?.split("").join(" ")}`
      );
      speakingRef.current = true;
      speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(message);
      utterance.lang = "en-IN";
      utterance.rate = 0.8;
      utterance.pitch = 1;

      if (indianVoice) utterance.voice = indianVoice;

      utterance.onend = () => {
        speakingRef.current = false;
        setOpen(false);
        showNextMessage();
      };

      speechSynthesis.speak(utterance);
    },
    [indianVoice, muted, showNotification]
  );

  /** 📢 Show next queued message */
  const showNextMessage = useCallback(() => {
    if (queueRef.current.length === 0) return;
    const next = queueRef.current.shift();
    setAlertData(next);
    setOpen(true);
    if (showNotification) {
      speakMessage(next.message);
    }
  }, [speakMessage, showNotification]);

  /** 📡 Setup WebSocket connection */
  useEffect(() => {
    if (!enabled || clientRef.current) return;

    const client = new Client({
      webSocketFactory: () => new SockJS(config.webSocketUrl),
      reconnectDelay: 5000,

      onConnect: () => {
        const subscription = client.subscribe("/topic/events", (message) => {
          const event = JSON.parse(message.body);
          const sessionData = JSON.parse(sessionStorage.getItem("data"));
          const userType = sessionData?.userType;
          const sites = sessionData?.sites || [];
          if (userType === "USER") {
            const hasSiteAccess = sites.some(
              (site) => site.id === event?.site?.id
            );
            if (!hasSiteAccess) return; // Skip if event not relevant
          }

          const msg = event?.message || "🔔 New Alarm!";
          queueRef.current.push({ type: "info", message: msg });
setEvents((prev) => [...prev, event].sort((a, b) => 
  new Date(b.eventTime) - new Date(a.eventTime)
));
          if (!speakingRef.current) showNextMessage();
        });

        clientRef.current = { client, subscription };
      },

      onDisconnect: () => console.log("❌ Disconnected from WebSocket"),
    });

    client.activate();

    return () => {
      if (clientRef.current) {
        clientRef.current.subscription?.unsubscribe();
        clientRef.current.client?.deactivate();
        clientRef.current = null;
      }
    };
  }, [enabled, showNextMessage]);

  /** Snackbar close handler */
  const handleClose = useCallback(
    (event, reason) => {
      if (reason === "clickaway") return;
      setOpen(false);
      speakingRef.current = false;
      showNextMessage();
    },
    [showNextMessage]
  );

  /** 🔇 Toggle mute */
  const toggleMute = useCallback(() => {
    setMuted((prev) => !prev);
    if (!muted) {
      speechSynthesis.cancel();
      speakingRef.current = false;
    }
  }, [muted]);

  /** ➖ Toggle minimize */
  const toggleMinimize = useCallback(() => {
    setMinimized((prev) => !prev);
  }, []);

  return (
    <>
      {showNotification && (
        <Snackbar
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          style={{ marginLeft: "60px", zIndex: 100 }}
        >
          <div
            style={{
              background: "#fff",
              color: "#333",
              padding: "12px 16px",
              borderRadius: "12px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
              maxWidth: minimized ? "160px" : "320px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: minimized ? "0" : "6px",
              borderLeft: "6px solid #d32f2f",
              fontFamily: "Segoe UI, sans-serif",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong style={{ color: "#d32f2f", fontSize: "1rem" }}>
                🚨 Alarm
              </strong>
              <div>
                {/* Minimize/Expand Button */}
                <Tooltip title={minimized ? "Expand" : "Minimize"}>
                  <IconButton
                    size="small"
                    onClick={toggleMinimize}
                    style={{ padding: "2px", color: "#666" }}
                  >
                    {minimized ? (
                      <AddIcon fontSize="small" />
                    ) : (
                      <RemoveIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>

                {/* Mute/Unmute Button */}
                <Tooltip title={muted ? "Unmute" : "Mute"}>
                  <IconButton
                    size="small"
                    onClick={toggleMute}
                    style={{
                      padding: "2px",
                      color: muted ? "#d32f2f" : "#666",
                    }}
                  >
                    {muted ? (
                      <NotificationsOffIcon fontSize="small" />
                    ) : (
                      <NotificationsActiveIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>

                {/* Close Button */}
                <IconButton
                  size="small"
                  onClick={handleClose}
                  style={{ padding: "2px", color: "#666" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>

            {/* Only show details if not minimized */}
            {!minimized && (
              <>
                {/* Message */}
                <div
                  style={{
                    fontSize: "0.95rem",
                    lineHeight: 1.4,
                    color: "#444",
                    wordWrap: "break-word",
                  }}
                >
                  {alertData.message}
                </div>

                {/* Timestamp */}
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#999",
                    textAlign: "right",
                  }}
                >
                  {new Date().toLocaleTimeString()}
                </div>
              </>
            )}
          </div>
        </Snackbar>
      )}
    </>
  );
});

export default NotificationComponent;
